import { useEffect, useMemo, useRef, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { articles, categories, siteInfo } from "@/mocks/news";
import { searchArticles } from "@/utils/news";
import BreakingTicker from "@/components/feature/BreakingTicker";

const breakingItems = [...articles]
  .sort((a, b) => b.hot - a.hot)
  .slice(0, 6)
  .map((item) => ({ id: item.id, title: item.title }));

export default function Header() {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);

  const results = useMemo(() => searchArticles(keyword, 5), [keyword]);

  useEffect(() => {
    const onClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setSearchOpen(false);
      }
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  const goArticle = (id: string) => {
    setKeyword("");
    setSearchOpen(false);
    setMenuOpen(false);
    navigate(`/article/${id}`);
  };

  const navClass = ({ isActive }: { isActive: boolean }) =>
    `whitespace-nowrap text-sm transition ${
      isActive
        ? "font-medium text-primary-600"
        : "text-foreground-700 hover:text-primary-600"
    }`;

  return (
    <header className="sticky top-0 z-50 border-b border-background-200 bg-background-50/95 backdrop-blur">
      <BreakingTicker items={breakingItems} />

      <div className="flex h-16 items-center gap-4 border-b border-background-200/70 px-4 md:px-6">
        <Link to="/" className="flex shrink-0 items-center gap-2.5" onClick={() => setMenuOpen(false)}>
          <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-500 text-base font-bold text-background-50">
            51
          </span>
          <span className="flex flex-col leading-none">
            <span className="font-serif-cn text-lg font-bold tracking-tight text-foreground-950">
              {siteInfo.name}
            </span>
            <span className="mt-0.5 hidden text-[11px] tracking-widest text-foreground-500 sm:block">
              {siteInfo.slogan}
            </span>
          </span>
        </Link>

        <nav aria-label="主导航" className="ml-2 hidden items-center gap-6 lg:flex">
          <NavLink to="/" end className={navClass}>
            首页
          </NavLink>
          <NavLink to="/category/kuaixun" className={navClass}>
            即时快讯
          </NavLink>
          <NavLink to="/category/tufa" className={navClass}>
            突发消息
          </NavLink>
          <NavLink to="/category/redian" className={navClass}>
            热点追踪
          </NavLink>
          <NavLink to="/about" className={navClass}>
            关于我们
          </NavLink>
        </nav>

        <div className="ml-auto flex items-center gap-2">
          <div ref={searchRef} className="relative hidden md:block">
            <div className="flex h-9 items-center gap-2 rounded-md border border-background-300 bg-background-100 px-3">
              <i className="ri-search-line text-sm text-foreground-500" />
              <input
                type="text"
                value={keyword}
                onChange={(event) => {
                  setKeyword(event.target.value);
                  setSearchOpen(true);
                }}
                onFocus={() => setSearchOpen(true)}
                placeholder="搜索爆料 / 快讯 / 热点"
                aria-label="站内搜索"
                className="w-44 bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-500 lg:w-56"
              />
            </div>
            {searchOpen && keyword.trim() && (
              <div className="absolute right-0 top-11 w-80 overflow-hidden rounded-lg border border-background-200 bg-background-50 p-2">
                {results.length > 0 ? (
                  results.map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => goArticle(item.id)}
                      className="flex w-full cursor-pointer items-start gap-2 rounded-md px-3 py-2 text-left transition hover:bg-background-100"
                    >
                      <i className="ri-file-text-line mt-0.5 text-sm text-primary-500" />
                      <span className="line-clamp-2 text-sm text-foreground-800">
                        {item.title}
                      </span>
                    </button>
                  ))
                ) : (
                  <p className="px-3 py-4 text-center text-sm text-foreground-500">
                    没有找到相关内容，换个关键词试试
                  </p>
                )}
              </div>
            )}
          </div>

          <button
            type="button"
            aria-label="搜索"
            onClick={() => setSearchOpen((value) => !value)}
            className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md border border-background-300 text-foreground-700 transition hover:border-primary-400 hover:text-primary-600 md:hidden"
          >
            <i className="ri-search-line text-lg" />
          </button>

          <button
            type="button"
            aria-label="菜单"
            onClick={() => setMenuOpen((value) => !value)}
            className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md border border-background-300 text-foreground-700 transition hover:border-primary-400 hover:text-primary-600 lg:hidden"
          >
            <i className={menuOpen ? "ri-close-line text-xl" : "ri-menu-line text-xl"} />
          </button>
        </div>
      </div>

      <div className="hidden items-center gap-2 overflow-x-auto px-4 py-2 md:flex md:px-6">
        <span className="flex shrink-0 items-center gap-1 text-xs font-medium text-foreground-500">
          <i className="ri-layout-grid-line" />
          栏目
        </span>
        {categories.map((item) => (
          <Link
            key={item.slug}
            to={`/category/${item.slug}`}
            className="flex shrink-0 items-center gap-1.5 rounded-full border border-background-300 bg-background-50 px-3 py-1 text-xs text-foreground-700 transition hover:border-primary-400 hover:bg-primary-50 hover:text-primary-600"
          >
            <i className={item.icon} />
            {item.name}
          </Link>
        ))}
      </div>

      {searchOpen && (
        <div className="border-b border-background-200 bg-background-100 px-4 py-3 md:hidden">
          <div className="flex h-10 items-center gap-2 rounded-md border border-background-300 bg-background-50 px-3">
            <i className="ri-search-line text-sm text-foreground-500" />
            <input
              type="text"
              value={keyword}
              onChange={(event) => setKeyword(event.target.value)}
              placeholder="搜索爆料 / 快讯 / 热点"
              aria-label="站内搜索"
              className="w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-500"
            />
          </div>
          {keyword.trim() && (
            <div className="mt-2">
              {results.length > 0 ? (
                results.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => goArticle(item.id)}
                    className="flex w-full cursor-pointer items-start gap-2 rounded-md px-2 py-2 text-left transition hover:bg-background-200"
                  >
                    <i className="ri-file-text-line mt-0.5 text-sm text-primary-500" />
                    <span className="line-clamp-2 text-sm text-foreground-800">{item.title}</span>
                  </button>
                ))
              ) : (
                <p className="px-2 py-3 text-sm text-foreground-500">没有找到相关内容</p>
              )}
            </div>
          )}
        </div>
      )}

      {menuOpen && (
        <nav
          aria-label="移动端导航"
          className="border-b border-background-200 bg-background-50 px-4 py-3 lg:hidden"
        >
          <div className="grid grid-cols-2 gap-2">
            <Link
              to="/"
              onClick={() => setMenuOpen(false)}
              className="rounded-md bg-background-100 px-3 py-2.5 text-sm text-foreground-800 transition hover:bg-primary-50 hover:text-primary-600"
            >
              首页
            </Link>
            {categories.map((item) => (
              <Link
                key={item.slug}
                to={`/category/${item.slug}`}
                onClick={() => setMenuOpen(false)}
                className="flex items-center gap-1.5 rounded-md bg-background-100 px-3 py-2.5 text-sm text-foreground-800 transition hover:bg-primary-50 hover:text-primary-600"
              >
                <i className={item.icon} />
                {item.name}
              </Link>
            ))}
            <Link
              to="/about"
              onClick={() => setMenuOpen(false)}
              className="col-span-2 rounded-md bg-foreground-950 px-3 py-2.5 text-center text-sm text-background-50 transition hover:bg-primary-600"
            >
              关于我们
            </Link>
          </div>
        </nav>
      )}
    </header>
  );
}